<template>
  <div class="bakune-top" role="main" aria-label="BAKUNEトップページ">
    <OrganismsLoading v-show="isLoading" />
    <!-- タブ -->
    <FeaturesHowtochooseRecoverywearSleepitemPartsTab />
    <!-- メイン画像 -->
    <FeaturesHowtochooseRecoverywearSleepitemFirstView :is-loaded="!isLoading" />
    <!-- 背景のフェードインしてくるBAKUNEの文字 -->
    <div class="interview__scroll_height"></div>
    <div class="interview__bg"></div>
    <p class="display__text__interview">BAKUNE</p>
    <!-- ジャマで、疲労回復。 -->
    <FeaturesHowtochooseRecoverywearSleepitemIntroduction @open-modal="modalVisible = true" />
    <!-- 人気の商品 -->
    <FeaturesHowtochooseRecoverywearSleepitemProductList :popular-products="popularProducts" />
    <!-- BAKUNEのこだわり -->
    <FeaturesHowtochooseRecoverywearSleepitemFeature />
    <!-- 着心地に合わせてシルエットをえらぼう -->
    <FeaturesHowtochooseRecoverywearSleepitemSilhouette />
    <!-- あなたが求める最高のパジャマ素材を -->
    <FeaturesHowtochooseRecoverywearSleepitemTextile />
    <!-- 好きな色でおうち時間を楽しむ -->
    <FeaturesHowtochooseRecoverywearSleepitemColor :products="mainProducts" />
    <!-- 自分好みの商品を見つけよう -->
    <FeaturesHowtochooseRecoverywearSleepitemFavorite :products="mainProducts" @update-favorite="updateFavorite" />
    <!-- 商品を比較する -->
    <FeaturesHowtochooseRecoverywearSleepitemCompare :products="mainProducts" :selected="compareSelected" />
    <!-- 2025春夏新用品おすすめラインナップ -->
    <FeaturesHowtochooseRecoverywearSleepitemRecommendLineup :products="recommendLineupProducts" />
    <!-- 人気の商品 -->
    <FeaturesHowtochooseRecoverywearSleepitemProductList :popular-products="popularProducts" />

    <!-- 「全ての商品を見る」モーダル -->
    <MoleculesConsumerModal
      :model-value="modalVisible"
      :enable-browser-back-close="false"
      max-width="1200px"
      @update:model-value="modalVisible = false"
    >
      <div class="lineup_modal_container">
        <h2>春夏におすすめの商品</h2>
        <div class="product__wrap">
          <FeaturesHowtochooseRecoverywearSleepitemPartsLineupModalContentItem
            v-for="product in lineupProducts.ss"
            :key="product.document_id"
            :product="product"
          />
        </div>
        <h2>秋冬におすすめの商品</h2>
        <div class="product__wrap">
          <FeaturesHowtochooseRecoverywearSleepitemPartsLineupModalContentItem
            v-for="product in lineupProducts.fw"
            :key="product.document_id"
            :product="product"
          />
        </div>
      </div>
    </MoleculesConsumerModal>
  </div>
</template>

<script setup lang="ts">
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import type {
  APIResponse,
  BakuneTopProduct,
  SearchConditionSelected,
  RecommendLineupProduct,
  LineupProducts,
} from "~/utils/functions/howtochoose_recoverywear_sleepitem"
import type { PagePartsProductType } from "~/types/product"
import { useCustomMeta } from "~/composables/meta"
import { useToast } from "~/state"

const toast = useToast()

gsap.registerPlugin(ScrollTrigger)

definePageMeta({ layout: "default-with-extended-footer" })

const customMeta = useCustomMeta()

useHead(
  customMeta.fetch({
    title: "リカバリーウェア「BAKUNE」の選び方｜素材・効果・ラインナップを徹底解説",
    description:
      "リカバリーウェア「BAKUNE」の効果や素材、シルエットの違いをわかりやすく解説。疲労回復をサポートする生地の特徴やラインナップを比較し、あなたにぴったりのリカバリーウェア選びをサポートします。",
    image: "/images/bakune/ogp-image.jpg",
  }),
)

const isLoading = ref(true)
const popularProducts: Ref<PagePartsProductType[]> = ref([])
const mainProducts: Ref<BakuneTopProduct[]> = ref([])
const recommendLineupProducts: Ref<RecommendLineupProduct[]> = ref([])
const lineupProducts: Ref<LineupProducts> = ref({
  ss: [],
  fw: [],
})
const compareSelected: Ref<SearchConditionSelected> = ref({
  sex: "unisex",
  silhouette: "",
  fabric: "",
  sleevesAndPants: "",
  detail: "",
})
const modalVisible = ref(false)

const updateFavorite = (searchConditionSelected: SearchConditionSelected) => {
  compareSelected.value = searchConditionSelected
}

const loadProducts = async () => {
  try {
    const response = await $fetch<APIResponse>("/api/_c/page/features/howtochoose_recoverywear_sleepitem", {
      method: "GET",
    })
    popularProducts.value = response.popularProducts
    mainProducts.value = response.mainProducts
    recommendLineupProducts.value = response.recommendLineupProducts
    lineupProducts.value = response.lineupProducts
  } catch (error) {
    toast?.showErrorToasted("商品情報の取得に失敗しました")
    console.error(error)
  }
}

await useLazyAsyncData("loadProducts", async () => {
  await loadProducts()
})

onMounted(() => {
  gsap.to(".display__text__interview", {
    visibility: "hidden",
    scrollTrigger: {
      trigger: ".section__feature",
      start: "top top",
      end: "top -30%",
      scrub: true,
    },
  })

  gsap.to(".display__text__interview", {
    opacity: "0.1",
    transform: "translate(0%,0%)scale(1)",
    scrollTrigger: {
      trigger: ".interview__scroll_height",
      start: "25% top",
      end: `+=${window.innerHeight}`,
      toggleActions: "play none play reverse",
      scrub: 0.1,
      anticipatePin: 1,
      fastScrollEnd: true,
    },
  })
  isLoading.value = false
})

onUnmounted(() => {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
})
</script>

<style scoped lang="scss">
@import "assets/scss/pages/howtochoose_recoverywear_sleepitem/main";
</style>
